import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import HomePage from './pages/HomePage';
import SolutionsPage from './pages/SolutionsPage';
import SolutionDetailPage from './pages/SolutionDetailPage';
import AIBasicsPage from './pages/AIBasicsPage';
import ContactPage from './pages/ContactPage';
import TeamPage from './pages/TeamPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { AnimatePresence } from 'framer-motion';
import { ModalProvider } from './context/ModalContext';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import AdminLayout from './layouts/AdminLayout';

// Admin pages
import DashboardPage from './pages/admin/DashboardPage';
import AdminSolutionsPage from './pages/admin/SolutionsPage';
import SolutionEditPage from './pages/admin/SolutionEditPage';
import ContactsPage from './pages/admin/ContactsPage';
import UsersPage from './pages/admin/UsersPage';
import SettingsPage from './pages/admin/SettingsPage';

// ScrollToTop component to ensure scroll position is reset on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <ModalProvider>
          <Router>
            <ScrollToTop />
            <AnimatePresence mode="wait">
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/solutions" element={<SolutionsPage />} />
                <Route path="/solutions/:id" element={<SolutionDetailPage />} />
                <Route path="/ai-basics" element={<AIBasicsPage />} />
                <Route path="/team" element={<TeamPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/reset-password/:resetToken" element={<ResetPasswordPage />} />
                
                {/* Admin routes - protected by authentication and admin role */}
                <Route element={<ProtectedRoute requireAdmin={true} />}>
                  <Route element={<AdminLayout />}>
                    <Route path="/admin/dashboard" element={<DashboardPage />} />
                    <Route path="/admin/solutions" element={<AdminSolutionsPage />} />
                    <Route path="/admin/solutions/new" element={<SolutionEditPage />} />
                    <Route path="/admin/solutions/:id" element={<SolutionEditPage />} />
                    <Route path="/admin/contacts" element={<ContactsPage />} />
                    <Route path="/admin/users" element={<UsersPage />} />
                    <Route path="/admin/settings" element={<SettingsPage />} />
                  </Route>
                </Route>
                
                {/* Fallback route */}
                <Route path="*" element={<HomePage />} />
              </Routes>
            </AnimatePresence>
          </Router>
        </ModalProvider>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
